/**
 * shared.tsx — Shared text-rendering utilities for the cms_renderer system.
 *
 * Key exports:
 *  - `transformDescription`   — Entry point for rendering text fields from CMS JSON.
 *    Handles both single strings and string arrays.
 *  - `transformTextToHtml`    — Parses and applies CMS-specific text directives
 *    (STYLE=..., CLASS=..., ELINE, SPACE=..., LINE=..., inline tags) before
 *    delegating to `transformMarkdown`.
 *  - `transformMarkdown`      — Renders Markdown text using ReactMarkdown with
 *    custom `p` and `a` renderers for class/style injection.
 *  - `extractKeyStyles`       — Converts Panel key suffix shorthands (e.g., `center`,
 *    `bold`, `Lora`, `h400px`) into a `React.CSSProperties` object.
 *  - `extractStylesFromUrl`   — Parses CSS properties embedded in a URL hash
 *    (e.g., `image.jpg#height=300px&border-radius=8px`).
 *  - `parseDimension`         — Normalises dimension values to numbers (strips `px`).
 *  - `handleProductSelection` — Shared analytics click handler for product cards.
 */

// Shared utilities and helpers for cms_renderer

import React from "react";
import ReactMarkdown from 'react-markdown';
import cssStyles from "./cms_renderer.module.scss";
import { trackItem } from "@/utils";
import { UICompType, ComponentRenderProps } from "./index";

// For unique keys in transformTextToHtml
let CMS_ID_COUNTER = 0;

/**
 * Entry point for rendering a CMS text value.
 *
 * Accepts either a single string or an array of strings. Arrays are mapped
 * individually so each string becomes its own element.
 *
 * All text directive processing happens inside `transformTextToHtml`:
 *   ELINE, SPACE=..., LINE=..., (STYLE=...), <STYLE=...>, (CLASS=...), inline tags.
 *
 * @param items          - The text content (string or string[]).
 * @param panelClassName - CSS module class name applied to each element.
 * @param trackId        - HTML `id` prefix for rendered elements.
 * @param style          - Inline React CSS styles merged onto each element.
 * @param elemType       - HTML tag for the wrapper element (default: `'p'`).
 * @param props          - `ComponentRenderProps` passed down for context.
 * @param deviceType     - `'Mobile'` triggers `mStyles` inside STYLE directives.
 * @returns Rendered React nodes.
 */
export function transformDescription(
    items: string[] | string | undefined,
    panelClassName?: string,
    trackId?: any,
    style?: React.CSSProperties,
    elemType?: string,
    props?: ComponentRenderProps,
    deviceType?: string
): React.ReactNode {
    if (Array.isArray(items)) {
        return items.map((descItem: string, index: number) => (
            transformTextToHtml(descItem, panelClassName, trackId + index, style, elemType, props, deviceType)
        ));
    } else if (typeof items === 'string') {
        return transformTextToHtml(items, panelClassName, trackId, style, elemType, props, deviceType);
    } else {
        console.error('Invalid items type:', typeof items);
        return null;
    }
}

/**
 * Renders a Markdown string using ReactMarkdown with custom component overrides.
 *
 * Custom overrides:
 *  - `p` — Replaced with the element specified by `elemTypeKey`, applying
 *    `className` and `style` from the CMS directives.
 *  - `a` — Styled link with optional `aStyle` and tracking-safe props.
 *
 * @param text         - Markdown-formatted string.
 * @param classNameStr - CSS module class name (resolved via `cssStyles`).
 * @param elemTypeKey  - HTML intrinsic element to use instead of `<p>`.
 * @param id           - HTML `id` for the element.
 * @param style        - Inline CSS styles for the element.
 * @param aStyle       - Inline CSS styles applied specifically to `<a>` tags.
 * @param componentProps - Passed through for future extensibility.
 */
export function transformMarkdown(text: string, classNameStr: string, elemTypeKey: keyof JSX.IntrinsicElements, id?: string, style?: React.CSSProperties, aStyle?: React.CSSProperties, componentProps?: ComponentRenderProps) {

    return (
        <ReactMarkdown components={{
            p: ({ node, ...props }) => {
                return React.createElement(
                    elemTypeKey,
                    { id, className: cssStyles[classNameStr] || classNameStr, style: { ...style, margin: '2px' } as any },
                    props.children
                );
            },
            a: ({ node, ref, ...props }) => <a key={id + "" || 'link'}
                className={cssStyles[classNameStr] || classNameStr}
                id={id}
                style={{ color: 'black', textDecoration: 'none', ...aStyle } as any}
                {...props} />
        }}>{text}</ReactMarkdown>
    );
}

/**
 * Parses CMS text directives and converts them to React elements.
 *
 * Directive processing order:
 *  1. `ELINE`                      — Returns an empty `<div>&nbsp;</div>`.
 *  2. `(SPACE={...})`              — Returns a `<div>` with the given height style.
 *  3. `(LINE={...})`               — Returns an `<hr>` with the given styles.
 *  4. `(STYLE={...})text(/STYLE)`  — Extracts inline styles (supports `aStyles` for
 *                                     link styling and `mStyles` for mobile).
 *  5. `<STYLE={...}>text</STYLE>`  — HTML-tag variant of `(STYLE=...)`.
 *  6. `(CLASS='k1_k2')text(/CLASS)`— Extracts style shorthands via `extractKeyStyles`.
 *  7. `<SMALL>`, `<LARGE>`, `<CENTER>`, `<BOLD>` — Quick inline wrappers.
 *  8. Bare text                    — Passed directly to `transformMarkdown`.
 *
 * @param text           - Raw CMS text string to process.
 * @param panelClassName - CSS module class applied to the rendered element.
 * @param id             - HTML `id` for the element.
 * @param style          - Base inline CSS styles.
 * @param elemType       - HTML tag for the element (default: `'p'`).
 * @param props          - `ComponentRenderProps` for context.
 * @param deviceType     - `'Mobile'` enables `mStyles` inside `(STYLE=...)`.
 * @returns A React node or `null` for empty text.
 */
export const transformTextToHtml = (
    text: string,
    panelClassName?: string,
    id?: string,
    style?: React.CSSProperties,
    elemType: string = 'p',
    props?: ComponentRenderProps,
    deviceType?: string
) => {
    if (!text) {
        return null;
    }

    if (text === 'ELINE') {
        return <div key={id || CMS_ID_COUNTER++} style={{ height: '1em' }}>&nbsp;</div>;
    }
    const spaceMatch = text.match(/^\(SPACE=({.*?})\)$/i);
    if (spaceMatch) {
        try {
            const styleObject = JSON.parse(spaceMatch[1].replace(/'/g, '"'));
            return <div key={id || CMS_ID_COUNTER++} style={styleObject} />;
        } catch (e) {
            console.error("Error parsing SPACE style: ", e);
        }
    }

    const lineMatch = text.match(/^\(LINE=({.*?})\)$/i);
    if (lineMatch) {
        try {
            const styleObject = JSON.parse(lineMatch[1].replace(/'/g, '"'));
            return <hr key={id || CMS_ID_COUNTER++} style={styleObject} />;
        } catch (e) {
            console.error("Error parsing LINE style: ", e);
        }
    }

    if (!panelClassName) {
        panelClassName = "";
    }

    let elemTypeKey: keyof JSX.IntrinsicElements = elemType as keyof JSX.IntrinsicElements;

    try {
        let styleMatch = text.match(/\(STYLE=({.*?})\)(.*?)\(\/STYLE\)/i);
        if (styleMatch) {
            let jsonObj = JSON.parse(styleMatch[1]?.replace(/'/g, '"') || '{}');
            if (jsonObj['class']) {
                const classNameValue = jsonObj['class'];
                delete jsonObj['class'];
                const extractedStyles = extractKeyStyles(classNameValue.split('_'));
                jsonObj = { ...jsonObj, ...extractedStyles };
            }
            let styleObject: { aStyles?: React.CSSProperties, mStyles?: React.CSSProperties } = jsonObj;
            const aStyleObject: React.CSSProperties = styleObject?.aStyles || {};
            let mStyleObject: React.CSSProperties = deviceType === "Mobile" && styleObject?.mStyles ? styleObject?.mStyles : {};

            // Remove non-CSS keys before spreading into inline styles
            delete (styleObject as any).aStyles;
            delete (styleObject as any).mStyles;
            delete (styleObject as any).aId;

            const aId = (jsonObj as any).aId || id;
            return transformMarkdown(styleMatch[2], panelClassName, elemTypeKey, aId, { ...style, ...styleObject, ...mStyleObject }, aStyleObject, props);
        }

        styleMatch = text.match(/<STYLE=({.*?})>(.*?)<\/STYLE>/i);
        if (styleMatch) {
            let styleObject: { aStyles?: React.CSSProperties, class?: string } = JSON.parse(styleMatch[1]?.replace(/'/g, '"') || '{}');
            const aStyleObject = (styleObject as { aStyles?: React.CSSProperties })?.aStyles || {};
            if (styleObject['class']) {
                const classNameValue = styleObject['class'];
                delete styleObject['class'];
                const extractedStyles = extractKeyStyles(classNameValue.split('_'));
                styleObject = { ...styleObject, ...extractedStyles };
            }
            return transformMarkdown(styleMatch[2], panelClassName, elemTypeKey, id, { ...style, ...styleObject }, aStyleObject, props);
        }

        let classMatch = text.match(/\(CLASS='(.*?)'\)(.*?)\(\/CLASS\)/i);
        if (classMatch) {
            const className = classMatch[1];
            const extractedStyles = extractKeyStyles(className.split('_'));
            return transformMarkdown(classMatch[2], className, elemTypeKey, id, { ...style, ...extractedStyles }, undefined, props);
        }

        if (text.match(/^<SMALL>/i)) {
            return transformMarkdown(text.replace(/<SMALL>/i, '').replace(/<\/SMALL>/i, ''), panelClassName + "SmallText", elemTypeKey, id, style, undefined, props)
        } else if (text.match(/^<LARGE>/i)) {
            return transformMarkdown(text.replace(/<LARGE>/i, '').replace(/<\/LARGE>/i, ''), panelClassName + "LargeText", elemTypeKey, id, style, undefined, props)
        } else if (text.match(/^<CENTER>/i)) {
            return transformMarkdown(text.replace(/<CENTER>/i, '').replace(/<\/CENTER>/i, ''), panelClassName + "CenterText", elemTypeKey, id, { ...style, textAlign: 'center' }, undefined, props);
        } else if (text.match(/^<BOLD>/i)) {
            return transformMarkdown(text.replace(/<BOLD>/i, '').replace(/<\/BOLD>/i, ''), panelClassName + "BoldText", elemTypeKey, id, { ...style, fontWeight: 'bold' }, undefined, props);
        } else if (text.match(/^ELINE$/i) || text.trim() === "") {
            return <div key={id}>&nbsp;</div>;
        } else {
            return transformMarkdown(text, panelClassName, elemTypeKey, id, style, undefined, props);
        }
    } catch (error) {
        throw new Error(`Failed to parse style object. Original error: ${error}. Input text: ${text}`);
    }
}

/**
 * Parses inline CSS properties and special flags embedded in a URL hash.
 *
 * **URL hash format:** `https://example.com/image.jpg#height=300px&border-radius=8px&quality=90&preload=true`
 *
 * Special handled query params (removed from returned `stylesFromUrl`):
 *  - `quality`       — Parsed as integer, default `75`. Passed to Next.js `<Image quality>`.
 *  - `preload`       — Parsed as boolean (`'true'`). Maps to Next.js `priority`.
 *  - `unoptimized`   — Parsed as boolean. Disables Next.js image optimisation.
 *
 * All remaining hash params are returned as-is in `stylesFromUrl` and can be
 * spread directly into `React.CSSProperties`.
 *
 * @param urlStr - Image or video URL, optionally with a hash-encoded style string.
 * @returns Object with `urlStr` (cleaned URL), `stylesFromUrl`, `quality`, `preload`, `unoptimized`.
 */
export function extractStylesFromUrl(urlStr: string) {
    try {
        const url = new URL(urlStr);
        const params = new URLSearchParams(url?.hash?.substring(1));
        const stylesFromUrl = Object.fromEntries(params.entries());
        const quality = stylesFromUrl.quality ? parseInt(stylesFromUrl.quality, 10) : 75;
        if ('quality' in stylesFromUrl) {
            delete stylesFromUrl.quality;
        }
        const preload = stylesFromUrl.preload === 'true';
        if ('preload' in stylesFromUrl) {
            delete stylesFromUrl.preload;
        }
        const unoptimized = stylesFromUrl.unoptimized === 'true';
        if ('unoptimized' in stylesFromUrl) {
            delete stylesFromUrl.unoptimized;
        }
        url.hash = '';
        return { 'urlStr': url.toString(), 'stylesFromUrl': stylesFromUrl, 'quality': quality, 'preload': preload, 'unoptimized': unoptimized };
    } catch (error) {
        console.info("invalid URL:", error);
        return { 'urlStr': urlStr, 'stylesFromUrl': {} };
    }
}

export const parseDimension = (dimension?: string | number | undefined): number | undefined => {
    if (typeof dimension === 'string') {
        return parseInt(dimension.replace('px', ''), 10);
    }
    return dimension;
};

export const handleProductSelection = (event: any, card?: any, sectionName?: string, cardIndex?: any,) => {
    //console.log('Selected cardIndex: ' + cardIndex + "cardId : " + JSON.stringify(card) + "sectionName : " + sectionName);
    trackItem({
        id: card?.id,
        event: sectionName + 'Click',
        name: card?.name,
        section: sectionName
    });
}

/**
 * Converts an array of style shorthand tokens into a `React.CSSProperties` object.
 *
 * Used by the Panel component to parse dynamic key suffixes (e.g., `title-center-bold-Lora`)
 * and by the `(CLASS='...')` text directive.
 *
 * **Supported tokens:**
 *
 * | Token pattern | CSS property | Example |
 * |---|---|---|
 * | `left\|center\|right\|justify\|start\|end` | `textAlign` | `center` |
 * | `small\|medium\|large\|\d+px\|\d+em\|\d+rem` | `fontSize` | `18px` |
 * | `#hex\|red\|blue\|green\|black\|white` | `color` | `#0D908E` |
 * | `bold\|normal\|light\|\d{3}` | `fontWeight` | `bold` |
 * | `italic\|oblique` | `fontStyle` | `italic` |
 * | `underline\|line-through\|none` | `textDecoration` | `underline` |
 * | `Lora\|Nunito` | `fontFamily` | `Lora` |
 * | `h\d+(px\|em\|rem\|%)?` | `height` | `h400px` |
 * | `w\d+(px\|em\|rem\|%)?` | `width` | `w100%` |
 * | `p\d+(px\|...)?` | `padding` | `p10px` |
 * | `m\d+(px\|...)?` | `margin` | `m20px` |
 * | `pt/pb/pl/pr\d+` | `paddingTop/Bottom/Left/Right` | `pt5px` |
 * | `mt/mb/ml/mr\d+` | `marginTop/Bottom/Left/Right` | `mt10px` |
 * | `b/bt/bb/bl/br\d+` | `border/Top/Bottom/Left/Right` | `b1px` |
 *
 * @param styleParts - Array of shorthand tokens split from a `-` delimited key
 * @returns Partial `React.CSSProperties` object
 */
export function extractKeyStyles(styleParts: string[]): React.CSSProperties {
    const keyStyles = styleParts.reduce((acc, curr) => {
        if (curr.match(/^(left|center|right|justify|start|end)$/i)) {
            acc.textAlign = curr as React.CSSProperties['textAlign'];
        } else if (curr.match(/^(small|medium|large|\d+px|\d+em|\d+rem)$/i)) {
            acc.fontSize = curr;
        } else if (curr.match(/^(#(?:[0-9a-fA-F]{3}){1,2}|red|blue|green|black|white)$/i)) {
            acc.color = curr;
        } else if (curr.match(/^(bold|normal|light|\d{3})$/i)) {
            acc.fontWeight = curr as React.CSSProperties['fontWeight'];
        } else if (curr.match(/^(italic|oblique)$/i)) {
            acc.fontStyle = curr as React.CSSProperties['fontStyle'];
        } else if (curr.match(/^(underline|line-through|none)$/i)) {
            acc.textDecoration = curr as React.CSSProperties['textDecoration'];
        } else if (curr.match(/^(Lora|Nunito)$/i)) {
            acc.fontFamily = curr;
        } else if (curr.match(/^h(\d+)(px|em|rem|%)?$/i)) {
            acc.height = curr.replace(/^h/, '');
        } else if (curr.match(/^w(\d+)(px|em|rem|%)?$/i)) {
            acc.width = curr.replace(/^w/, '');
        } else if (curr.match(/^p(\d+)(px|em|rem|%)?$/i)) {
            acc.padding = curr.replace(/^p/, '');
        } else if (curr.match(/^m(\d+)(px|em|rem|%)?$/i)) {
            acc.margin = curr.replace(/^m/, '');
        } else if (curr.match(/^pt(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingTop = curr.replace(/^pt/, '');
        } else if (curr.match(/^pb(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingBottom = curr.replace(/^pb/, '');
        } else if (curr.match(/^pl(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingLeft = curr.replace(/^pl/, '');
        } else if (curr.match(/^pr(\d+)(px|em|rem|%)?$/i)) {
            acc.paddingRight = curr.replace(/^pr/, '');
        } else if (curr.match(/^mt(\d+)(px|em|rem|%)?$/i)) {
            acc.marginTop = curr.replace(/^mt/, '');
        } else if (curr.match(/^mb(\d+)(px|em|rem|%)?$/i)) {
            acc.marginBottom = curr.replace(/^mb/, '');
        } else if (curr.match(/^ml(\d+)(px|em|rem|%)?$/i)) {
            acc.marginLeft = curr.replace(/^ml/, '');
        } else if (curr.match(/^mr(\d+)(px|em|rem|%)?$/i)) {
            acc.marginRight = curr.replace(/^mr/, '');
        } else if (curr.match(/^b(\d+)(px|em|rem|%)?$/i)) {
            acc.border = curr.replace(/^b/, '');
        } else if (curr.match(/^bt(\d+)(px|em|rem|%)?$/i)) {
            acc.borderTop = curr.replace(/^bt/, '');
        } else if (curr.match(/^bb(\d+)(px|em|rem|%)?$/i)) {
            acc.borderBottom = curr.replace(/^bb/, '');
        } else if (curr.match(/^bl(\d+)(px|em|rem|%)?$/i)) {
            acc.borderLeft = curr.replace(/^bl/, '');
        } else if (curr.match(/^br(\d+)(px|em|rem|%)?$/i)) {
            acc.borderRight = curr.replace(/^br/, '');
        }
        return acc;
    }, {} as React.CSSProperties);
    return keyStyles;
}

// Export all types, helpers, and shared functions here.
// Example:
//export * from './types'; // If you want to split types into a types.ts file, or just define them here.